import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
} from "recharts";
import {
  CalendarDays,
  Download,
  Filter,
  DollarSign,
  Target,
  MousePointerClick,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { glass, BLUE, ORANGE, GREEN, RED } from "./shared";

const RANGES = ["7d", "30d", "90d", "1y"] as const;
type Range = (typeof RANGES)[number];

const buildSeries = (range: Range) => {
  const days = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : 365;
  const arr: { d: string; spend: number; conv: number }[] = [];
  for (let i = 0; i < days; i++) {
    const t = i / days;
    const spend = 200 + Math.sin(i / 4) * 80 + Math.cos(i / 9) * 60 + t * 280 + (i % 7 === 0 ? 90 : 0);
    const conv = 6 + Math.sin(i / 3 + 1) * 3 + Math.cos(i / 7) * 2 + t * 12 + (i % 5 === 0 ? 4 : 0);
    arr.push({
      d: new Date(Date.now() - (days - i) * 86400000).toLocaleDateString("en", { month: "short", day: "numeric" }),
      spend: Math.max(40, Math.round(spend)),
      conv: Math.max(1, Math.round(conv)),
    });
  }
  return arr;
};

const spark = (seed: number) =>
  Array.from({ length: 16 }, (_, i) => ({
    v: 30 + Math.sin(i / 2 + seed) * 14 + ((i * (seed % 4)) % 11),
  }));

const ROWS = [
  { name: "Brand Awareness Q2", spend: 1842, clicks: 3842, cpc: 0.48, conv: 78, roas: 5.11, status: "Enabled" },
  { name: "Lead Gen Search", spend: 4210, clicks: 5104, cpc: 0.82, conv: 184, roas: 6.84, status: "Enabled" },
  { name: "Retargeting Display", spend: 844, clicks: 1622, cpc: 0.52, conv: 33, roas: 4.88, status: "Enabled" },
  { name: "Performance Max — Spring", spend: 1320, clicks: 1248, cpc: 1.06, conv: 22, roas: 2.36, status: "Paused" },
  { name: "Local Service Ads", spend: 1806, clicks: 982, cpc: 1.84, conv: 61, roas: 6.2, status: "Enabled" },
];

const Metric = ({
  icon: Icon,
  label,
  value,
  delta,
  up,
  accent,
  seed,
}: {
  icon: any;
  label: string;
  value: string;
  delta: string;
  up: boolean;
  accent: string;
  seed: number;
}) => (
  <div className="rounded-2xl p-5 transition-all hover:-translate-y-0.5" style={glass}>
    <div className="flex items-center justify-between">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ background: `${accent}1f`, border: `1px solid ${accent}44`, color: accent }}
      >
        <Icon size={15} />
      </div>
      <div
        className="flex items-center gap-1 text-[11px] font-bold"
        style={{ color: up ? GREEN : RED }}
      >
        {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {delta}
      </div>
    </div>
    <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50">{label}</div>
    <div className="mt-1 text-[26px] font-bold tracking-tight text-white">{value}</div>
    <div className="mt-2 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={spark(seed)}>
          <defs>
            <linearGradient id={`pm-${seed}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.55} />
              <stop offset="100%" stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={2} fill={`url(#pm-${seed})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const PerformanceTab = () => {
  const [range, setRange] = useState<Range>("30d");
  const [campaign, setCampaign] = useState("all");
  const data = useMemo(() => buildSeries(range), [range]);

  const totals = useMemo(
    () => ({
      spend: ROWS.reduce((s, r) => s + r.spend, 0),
      conv: ROWS.reduce((s, r) => s + r.conv, 0),
      cpc: ROWS.reduce((s, r) => s + r.cpc, 0) / ROWS.length,
      roas: ROWS.reduce((s, r) => s + r.roas, 0) / ROWS.length,
    }),
    [],
  );

  return (
    <div className="space-y-5">
      {/* Top controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
            <CalendarDays size={13} className="ml-2 text-white/40" />
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className="rounded-md px-3 py-1.5 text-[11.5px] font-semibold transition-colors"
                style={{
                  background: range === r ? "rgba(255,255,255,0.08)" : "transparent",
                  color: range === r ? "#fff" : "rgba(255,255,255,0.55)",
                }}
              >
                {r}
              </button>
            ))}
          </div>
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <select
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              className="h-9 appearance-none rounded-lg border border-white/10 bg-white/[0.03] pl-8 pr-8 text-[12.5px] text-white focus:border-white/20 focus:outline-none"
            >
              <option value="all">All campaigns</option>
              {ROWS.map((r) => (
                <option key={r.name} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] font-semibold text-white/80 hover:bg-white/[0.06]"
          onClick={() => {
            const csv = ["Campaign,Spend,Clicks,CPC,Conv,ROAS,Status"]
              .concat(ROWS.map((r) => `${r.name},${r.spend},${r.clicks},${r.cpc},${r.conv},${r.roas},${r.status}`))
              .join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "performance.csv";
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download size={13} />
          Export
        </button>
      </div>

      {/* Chart */}
      <div data-tour-id="perf-chart" className="rounded-2xl p-5" style={glass}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-bold text-white">Spend vs Conversions</h3>
            <p className="text-[11.5px] text-white/50">Daily totals · {range}</p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-white/60">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: ORANGE }} /> Spend
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: GREEN }} /> Conversions
            </span>
          </div>
        </div>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="d" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" minTickGap={20} />
              <YAxis yAxisId="l" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
              <YAxis yAxisId="r" orientation="right" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
              <Tooltip
                contentStyle={{
                  background: "rgba(8,12,22,0.95)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 10,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#fff", fontWeight: 700 }}
              />
              <Line yAxisId="l" type="monotone" dataKey="spend" stroke={ORANGE} strokeWidth={2.2} dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="conv" stroke={GREEN} strokeWidth={2.2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Metric cards */}
      <div data-tour-id="perf-metrics" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric icon={DollarSign} label="Total Spend" value={`$${totals.spend.toLocaleString()}`} delta="3.1%" up accent={ORANGE} seed={11} />
        <Metric icon={Target} label="Total Conversions" value={String(totals.conv)} delta="27.9%" up accent={GREEN} seed={12} />
        <Metric icon={MousePointerClick} label="Avg CPC" value={`$${totals.cpc.toFixed(2)}`} delta="6.8%" up={false} accent={BLUE} seed={13} />
        <Metric icon={TrendingUp} label="ROAS" value={`${totals.roas.toFixed(2)}x`} delta="14.2%" up accent={GREEN} seed={14} />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl" style={glass}>
        <div className="border-b border-white/5 px-5 py-4">
          <h3 className="text-[15px] font-bold text-white">Campaign performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-[12.5px]">
            <thead className="bg-white/[0.02]">
              <tr>
                {["Campaign", "Spend", "Clicks", "CPC", "Conversions", "ROAS", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold uppercase tracking-[0.1em] text-[10.5px] text-white/45">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.name} className="border-t border-white/5 hover:bg-white/[0.025]">
                  <td className="px-4 py-3 font-semibold text-white">{r.name}</td>
                  <td className="px-4 py-3 text-white/80">${r.spend.toLocaleString()}</td>
                  <td className="px-4 py-3 text-white/80">{r.clicks.toLocaleString()}</td>
                  <td className="px-4 py-3 text-white/80">${r.cpc.toFixed(2)}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: GREEN }}>
                    {r.conv}
                  </td>
                  <td className="px-4 py-3 font-semibold" style={{ color: r.roas >= 3 ? GREEN : ORANGE }}>
                    {r.roas.toFixed(2)}x
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="rounded-full px-2 py-0.5 text-[10.5px] font-bold"
                      style={{
                        background: r.status === "Enabled" ? `${GREEN}22` : "rgba(255,255,255,0.06)",
                        color: r.status === "Enabled" ? GREEN : "rgba(255,255,255,0.55)",
                      }}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTab;
